"use client";
import React, { useCallback, useMemo, useState } from "react";
import stl from "./od-os.module.scss";
import { useCommonContext, usePrescriptionContext } from "@/src/provider";
import Help from "./Help";
import RxTitle from "../RxTitle";
import { FmIcon, FmInput, FmPicker } from "@/ui-component";
import RxSelectModal from "../RxSelect/RxSelectModal";
import classnames from "classnames";
import { IErrorList } from "@/src/productDetails/Rx/RxForm";
import variable from "@/src/styles/variables.module.scss";
import { useTranslation } from "@/src/i18n/client";
interface IProps {
    selectData: Record<string, number[]>;
    errors: IErrorList;
    onChange?: () => void;
}

export type EyeType =
    | "od_sph"
    | "od_cyl"
    | "od_axis"
    | "os_sph"
    | "os_cyl"
    | "os_axis";

interface ISphSyl {
    title: string;
    name: string;
    defaultValue: {
        label: string;
        value: string;
    };
}
export type IODOSConfigs = Record<EyeType, ISphSyl>;
const eyeType = {
    od: {
        title: "lens:Rx.OD",
        description: "lens:Rx.RightEyeOD",
    },
    os: {
        title: "lens:Rx.OS",
        description: "lens:Rx.LeftEyeOS",
    },
};
export const odosConfigs: IODOSConfigs = {
    od_sph: {
        title: "lens:OD_SPH",
        name: "od_sph",
        defaultValue: {
            label: "lens:Rx.None/PL",
            value: "lens:Rx.None/PL",
        },
    },
    od_cyl: {
        title: "lens:OD_CYL",
        name: "od_sph",
        defaultValue: {
            label: "lens:Rx.None/SPH",
            value: "lens:Rx.None/SPH",
        },
    },
    od_axis: {
        name: "od_axis",
        title: "lens:Rx.Axis",
        defaultValue: {
            label: "lens:Rx.None",
            value: "",
        },
    },
    os_sph: {
        title: "lens:OS_SPH",
        name: "od_sph",
        defaultValue: {
            label: "lens:Rx.None/PL",
            value: "lens:Rx.None/PL",
        },
    },
    os_cyl: {
        title: "lens:OS_CYL",
        name: "od_sph",
        defaultValue: {
            label: "lens:Rx.None/SPH",
            value: "lens:Rx.None/SPH",
        },
    },
    os_axis: {
        name: "os_axis",
        title: "lens:Rx.Axis",
        defaultValue: {
            label: "lens:Rx.None",
            value: "",
        },
    },
};

const ODOS: React.FC<IProps> = (props) => {
    const { site } = useCommonContext();
    const { t } = useTranslation(["lens"]);

    const [activeData, setPrescription] = usePrescriptionContext((state) => [
        state.activeData,
        state.setPrescription,
    ]);

    const [selectedEye, setSelectedEye] = useState<EyeType | null>(null);

    const odosTitle = [
        {
            title: t("lens:Rx.SPH"),
            help: (
                <Help
                    key={"rx-sph"}
                    // open={}
                    title={t("lens:prescriptions.Sphere")}
                    description={t("lens:prescriptions.SphereHelp")}
                    video={
                        site !== "fr"
                            ? "../../static/videos/SPH.mp4"
                            : undefined
                    }
                />
            ),
        },
        {
            title: t("lens:Rx.CYL"),
            help: (
                <Help
                    key={"rx-cyl"}
                    title={t("lens:prescriptions.Cylinderl")}
                    description={t("lens:prescriptions.CylinderlHelp")}
                    video={
                        site !== "fr"
                            ? "../../static/videos/CYL.mp4"
                            : undefined
                    }
                />
            ),
        },
        {
            title: t("lens:Rx.Axis"),
            help: (
                <Help
                    key={"rx-axis"}
                    video={
                        site !== "fr"
                            ? "../../static/videos/AXIS.mp4"
                            : undefined
                    }
                    title={t("lens:prescriptions.Axis")}
                    description={t("lens:prescriptions.AxisHelp")}
                />
            ),
        },
    ];

    const [pickerOpen, setPickerOpen] = useState(false);
    const [axisPickerOpen, setAxisPickerOpen] = useState({
        od_axis: false,
        os_axis: false,
    });
    // 点击弹窗，设置眼睛和度数类型
    const handleClickInput = useCallback(
        (e: React.MouseEvent<HTMLDivElement, MouseEvent>, key: EyeType) => {
            e.stopPropagation();
            e.preventDefault();
            setSelectedEye(key);
            setPickerOpen(true);
        },
        [],
    );

    const handleChange = (value: string | number | null) => {
        if (!selectedEye) return;
        const obj = { [selectedEye]: value };
        // axis不走这边
        if (
            /axis/.test(selectedEye) ||
            (/cyl/.test(selectedEye) && value === null)
        ) {
            obj[`${selectedEye.split("_")[0]}_axis`] = ""; // 对应的列axis为空
        }
        setPrescription(obj);
    };

    const aixsData = useMemo(() => {
        const tempAxisData: Record<"od_axis" | "os_axis", any> = {
            od_axis: [],
            os_axis: [],
        };
        let temp = [];
        for (let item of ["od_axis", "os_axis"] as const) {
            temp = [...props.selectData[item]];
            tempAxisData[item] = temp.map((item: string | number) => {
                return {
                    label: item,
                    value: item,
                };
            });
            tempAxisData[item].unshift({
                value: t(`${odosConfigs[item].defaultValue.value}`),
                label: t(`${odosConfigs[item].defaultValue.label}`),
            });
        }
        return tempAxisData;
    }, [props.selectData, t]);
    return (
        <div className={stl["od-os-container"]}>
            <div className={stl["content-title-column"]}>
                <div></div>
                {odosTitle.map((item) => (
                    <RxTitle
                        key={item.title}
                        title={item.title}
                        className={stl["odos-title"]}
                        classes={{
                            title: stl["od-os-content-title"],
                        }}
                    >
                        {item.help}
                    </RxTitle>
                ))}
                {(["od", "os"] as const).map((item: "od" | "os") => (
                    <>
                        <RxTitle
                            key={item}
                            title={t(eyeType[item].title)}
                            details={t(eyeType[item].description)}
                        />
                        {(["_sph", "_cyl", "_axis"] as const).map((key) => {
                            return key !== "_axis" ? (
                                <FmInput
                                    key={key}
                                    className={stl["picker-wrapper"]}
                                    defaultValue={t(
                                        `${odosConfigs[`${item}${key}`].defaultValue.value}`,
                                    )}
                                    onClick={(e) =>
                                        handleClickInput(e, `${item}${key}`)
                                    }
                                    borderColor={variable.main}
                                    onChange={props.onChange}
                                    name={`${item}${key}`}
                                    value={
                                        activeData.prescription[
                                            `${item}${key}` as EyeType
                                        ] ??
                                        t(
                                            `${odosConfigs[`${item}${key}`].defaultValue.value}`,
                                        )
                                    }
                                    endAdornment={
                                        <FmIcon
                                            icon={"icon--arrow-down"}
                                            className={
                                                stl["select-input-icon-down"]
                                            }
                                        />
                                    }
                                    classes={{
                                        root: classnames(
                                            stl["input-root"],
                                            stl[`${item}${key}`],
                                        ),
                                    }}
                                />
                            ) : (
                                <FmPicker
                                    error={
                                        props.errors[
                                            `${item.toUpperCase() as "OD" | "OS"}Error`
                                        ].trim().length > 0
                                    }
                                    helperText={
                                        props.errors[
                                            `${item.toUpperCase() as "OD" | "OS"}Error`
                                        ]
                                    }
                                    borderColor={variable.main}
                                    errorPosition="bottom"
                                    open={axisPickerOpen[`${item}${key}`]}
                                    dataSource={aixsData[`${item}${key}`]}
                                    inputClassName={classnames(stl["axis"])}
                                    classes={{
                                        root: classnames(
                                            stl["input-root"],
                                            stl[`${item}${key}`],
                                            "input-picker-akane",
                                            {
                                                [stl["disabled"]]:
                                                    /axis/.test(key) &&
                                                    !activeData.prescription[
                                                        `${item}_cyl`
                                                    ],
                                            },
                                        ),
                                    }}
                                    name={`${item}${key}`}
                                    disabled={
                                        /axis/.test(key) &&
                                        !activeData.prescription[`${item}_cyl`]
                                    }
                                    key={key}
                                    confirm="confirm"
                                    cancel="cancel"
                                    value={
                                        activeData.prescription[
                                            `${item}${key}`
                                        ] ||
                                        t(
                                            `${odosConfigs[`${item}${key}`].defaultValue.value}`,
                                        )
                                    }
                                    onChange={(open, value) => {
                                        setAxisPickerOpen((prev) => {
                                            prev[`${item}${key}`] = open;
                                            return prev;
                                        });
                                        setPrescription({
                                            [`${item}${key}`]: value,
                                        });
                                        props.onChange?.();
                                    }}
                                />
                            );
                        })}
                    </>
                ))}
            </div>

            {selectedEye && (
                <RxSelectModal
                    open={pickerOpen}
                    selected={selectedEye}
                    onChange={handleChange}
                    value={activeData.prescription?.[selectedEye] ?? null}
                    selectInit={props.selectData}
                    onClose={() => setPickerOpen(false)}
                />
            )}
        </div>
    );
};

export default ODOS;
